import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { ArrowLeftRight, Loader2 } from 'lucide-react';
import { formatCurrency } from '../../utils/currency';

export const InterBankTransferModal = ({ open, onOpenChange, bankAccounts = [], business, onSubmit }) => {
  const [fromBankId, setFromBankId] = useState('');
  const [toBankId, setToBankId] = useState('');
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!fromBankId || !toBankId || !amount || !date) return;
    if (fromBankId === toBankId) return;

    setSubmitting(true);
    try {
      await onSubmit({
        from_bank_account_id: fromBankId,
        to_bank_account_id: toBankId,
        amount: parseFloat(amount),
        date,
        description: description || undefined
      });
      // Reset form
      setFromBankId('');
      setToBankId('');
      setAmount('');
      setDescription('');
    } finally {
      setSubmitting(false);
    }
  };

  const fromBank = bankAccounts.find(b => b.id === fromBankId);
  const toBank = bankAccounts.find(b => b.id === toBankId);
  const availableToBanks = bankAccounts.filter(b => b.id !== fromBankId);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="transfer-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ArrowLeftRight className="w-5 h-5 text-[#283F8B]" />
            Transfer Between Banks
          </DialogTitle>
          <DialogDescription>
            Record a fund transfer between your bank accounts.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label>From Bank Account</Label>
            <Select value={fromBankId} onValueChange={setFromBankId}>
              <SelectTrigger data-testid="transfer-from-bank">
                <SelectValue placeholder="Select source bank" />
              </SelectTrigger>
              <SelectContent>
                {bankAccounts.map(bank => (
                  <SelectItem key={bank.id} value={bank.id}>
                    {bank.bank_name} {bank.account_number ? `(***${bank.account_number.slice(-4)})` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex justify-center">
            <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center">
              <ArrowLeftRight className="w-4 h-4 text-muted-foreground" />
            </div>
          </div>

          <div className="space-y-2">
            <Label>To Bank Account</Label>
            <Select value={toBankId} onValueChange={setToBankId}>
              <SelectTrigger data-testid="transfer-to-bank">
                <SelectValue placeholder="Select destination bank" />
              </SelectTrigger>
              <SelectContent>
                {availableToBanks.map(bank => (
                  <SelectItem key={bank.id} value={bank.id}>
                    {bank.bank_name} {bank.account_number ? `(***${bank.account_number.slice(-4)})` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Amount ({business?.currency || 'NGN'})</Label>
              <Input
                type="number"
                min="0.01"
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0.00"
                data-testid="transfer-amount"
              />
            </div>
            <div className="space-y-2">
              <Label>Date</Label>
              <Input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                data-testid="transfer-date"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Description (optional)</Label>
            <Input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="e.g. Transfer for payroll"
              data-testid="transfer-description"
            />
          </div>

          {fromBank && toBank && amount && (
            <div className="p-3 bg-muted/50 rounded-lg text-sm" data-testid="transfer-summary">
              <p className="font-medium">Summary:</p>
              <p className="text-muted-foreground mt-1">
                Transfer <span className="font-semibold text-foreground">{formatCurrency(parseFloat(amount), business?.currency)}</span> from{' '}
                <span className="font-medium">{fromBank.bank_name}</span> to{' '}
                <span className="font-medium">{toBank.bank_name}</span>
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!fromBankId || !toBankId || !amount || !date || fromBankId === toBankId || submitting}
            className="bg-[#283F8B] hover:bg-[#283F8B]/90 gap-2"
            data-testid="transfer-submit"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowLeftRight className="w-4 h-4" />}
            Record Transfer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
